// src/pages/SpecialAgencyCasesPage.tsx
//
// ★ PHASE 6, TASK 26 — Annex A story 35: "Record Management for Special
// Agencies/Cases".
//
// ⚠ The second Phase 6 surface (after story 36's MBIM records) NOT
// restricted to PSD — story 35 names its own actor as "User (Entitlement
// User)". Owned by @prs2/entitlement, administered but unwired from
// entitlement resolution and pro-ration — see
// `043_special_agency_case.sql`'s own header.
import { useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { VersionHistory } from '../components/VersionHistory'
import { FormResult } from '../components/FormResult'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

type SpecialAgencyCaseSortKey = 'caseCode' | 'agencyCode' | 'effectiveFrom' | 'officerId' | 'caseType'

const COLUMN_LABELS: Readonly<Record<SpecialAgencyCaseSortKey, string>> = {
  caseCode: 'Case code',
  agencyCode: 'Agency',
  officerId: 'Officer',
  caseType: 'Case type',
  effectiveFrom: 'Effective from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  caseCode: 'Case code',
  caseType: 'Case type',
  note: 'Note (optional)',
} as const

/**
 * ★★ WAVE 8 (Task 3) REVIEW FINDING — the bounded field that ONLY EXISTS ON
 * AN ESTABLISH, and is therefore not part of a RETIRE's problem set.
 *
 * ⚠ Ticking the "Retire … instead of superseding it" box unmounts it and drops
 * it from the request body, but the React state lives on this page and survives
 * the unmount. Without naming it here, a value typed before the box was ticked
 * keeps `problems.any` true and **disables Save with no error visible anywhere** —
 * the Wave 5 failure `components/FieldError.tsx`'s header records verbatim. See
 * `lib/published-bounds.ts`'s `BoundsProblemOptions` for the full reasoning.
 *
 * ⚠ This list is the SUBMITTED half of `handleSubmit`'s own `!retireInstead &&`
 * spread below. If one changes, the other has to.
 */
const ESTABLISH_ONLY_FIELDS = ['caseType'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

export function SpecialAgencyCasesPage() {
  // ★★★ WAVE 12 (P4) — the version history and its bi-temporal rewind. The series
  // route has answered since Phase 6 and NOTHING called it until this wave; the
  // RTM's Annex A 26-28 row carried that as a gap, because an endpoint no client
  // calls is indistinguishable from one that does not work.
  //
  // ⚠ The fetcher RESOLVES TO NULL until a code is chosen rather than calling the
  // route with an empty key: that URL is the LIST route, which would return the
  // whole catalogue into a component that renders it as one code's history.
  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(
    () =>
      historySubject.length === 0
        ? Promise.resolve(null)
        : api.specialAgencyCaseSeries(historySubject, historyAsRecordedAt),
    [historySubject, historyAsRecordedAt],
  )
  const { sort, onSort, interacted } = useSort<SpecialAgencyCaseSortKey>()
  const cases = useApi(() => api.specialAgencyCases(sortParams(sort)), [sort.column, sort.dir])

  const [caseCode, setCaseCode] = useState('')
  const [officerId, setOfficerId] = useState('')
  const [agencyCode, setAgencyCode] = useState('')
  const [caseType, setCaseType] = useState('')
  const [entitlementConfiguration, setEntitlementConfiguration] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = cases.data?.cases ?? []
  const total = cases.data?.total ?? 0

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = cases.data?.validation
  const problems = boundsProblems(
    validation,
    {
      caseCode: caseCode.trim().toUpperCase(),
      caseType: caseType.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
    // ⚠ A RETIRE sends none of the establish-only fields, so none of them can be
    // in breach — see ESTABLISH_ONLY_FIELDS above for the disabled-with-no-message
    // defect this closes.
    { notSubmitted: retireInstead ? ESTABLISH_ONLY_FIELDS : [] },
  )

  const canSubmit =
    caseCode.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    !problems.any &&
    (retireInstead || (officerId.trim().length > 0 && agencyCode.trim().length > 0 && caseType.trim().length > 0))

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). Composed once, from the fields as they were when Save was pressed —
   * never re-derived from a form the officer may since have edited.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const upper = caseCode.trim().toUpperCase()
    setConfirming(
      retireInstead
        ? `Retire special agency/case record ${upper} from ${effectiveFrom}. No successor opens, so from that day no case is in force under that code. There is no undo. A mistake is corrected by another version.`
        : `Supersede special agency/case record ${upper} from ${effectiveFrom}: officer ${officerId.trim()}, agency ${agencyCode.trim().toUpperCase()}, case type ${caseType.trim()}. The version in force closes on that day and this one takes its place; nothing here is read by entitlement resolution or the pro-ration engine. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeSpecialAgencyCase(caseCode.trim().toUpperCase(), {
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && {
          officerId: officerId.trim(),
          agencyCode: agencyCode.trim().toUpperCase(),
          caseType: caseType.trim(),
          entitlementConfiguration: entitlementConfiguration.trim().length > 0 ? entitlementConfiguration.trim() : null,
        }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${caseCode.trim().toUpperCase()} retired from ${effectiveFrom}.`
            : `${caseCode.trim().toUpperCase()} superseded from ${effectiveFrom}.`,
      })
      setCaseCode('')
      setOfficerId('')
      setAgencyCode('')
      setCaseType('')
      setEntitlementConfiguration('')
      setEffectiveFrom('')
      setNote('')
      cases.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this case.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Special agencies/cases</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            Special-agency and exceptional-case entitlement records. Administered by the
            Entitlement User, not PSD. Nothing here is read by entitlement resolution or the pro-ration
            engine; this is the administration surface for these records and nothing more. Effective-dated.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.specialAgencyCaseExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/special-agency-cases/export" />}
          />
        </div>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={cases.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {cases.error !== null ? (
        <ErrorCard title="Could not load special agency/case records" message={cases.error} onRetry={cases.reload} />
      ) : cases.initialLoading ? (
        <LoadingCard label="Loading special agency/case records" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No cases in scope"
          message="Either nothing is currently in force, or your active identity is not the Entitlement User: this catalogue is administered by the Entitlement User only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={cases.loading}>
            <table className="pt-table">
              <caption className="sr-only">Special agency/case records in force, with case code, officer, agency, case type and effective date</caption>
              <thead>
                <tr>
                  <SortableTh label="Case code" sortKey="caseCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Officer" sortKey="officerId" sort={sort} onSort={onSort} />
                  <SortableTh label="Agency" sortKey="agencyCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Case type" sortKey="caseType" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-semibold pt-mono">{row.caseCode}</td>
                    <td className="pt-mono">{row.officerId}</td>
                    <td>{row.agencyCode}</td>
                    <td className="text-sm">{row.caseType}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently in-force case{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
        </div>
      )}

      {/* ★★★ WAVE 12 (P4) — the version history, below the catalogue it belongs to.
          The code picker lives in the component rather than as a column on this
          table: twelve tables of differing shapes meant twelve thead/tbody edits,
          twelve chances to break a working screen for one control. */}
      <VersionHistory
        subject={historySubject}
        subjectOptions={rows.map((row) => row.caseCode)}
        subjectHint="The case codes listed above. Sort or filter that table to reach others."
        onSubjectChange={(next) => {
          // ⚠ Changing code RESETS the rewind: an instant chosen while reading one
          // code is not a statement about another, and carrying it over would
          // silently reconstruct the new one at a forgotten instant.
          setHistoryAsRecordedAt('')
          setHistorySubject(next)
        }}
        entries={history.data?.entries ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        asRecordedAt={historyAsRecordedAt}
        onAsRecordedAtChange={setHistoryAsRecordedAt}
        columns={[
            { header: 'Agency', cell: (entry) => entry.agencyCode },
        ]}
      />
      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Establish or retire a case</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            The officer and the agency must already be registered: checked before anything is written.
            Superseding is the only way a case changes: there is no edit-in-place.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <BoundedField
            label={BOUNDED_FIELDS.caseCode}
            example="e.g. CASE_DEMO_01."
            value={caseCode}
            onValueChange={setCaseCode}
            bounds={validation?.caseCode}
            problem={problems.of.caseCode}
            required
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Effective from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this case instead of superseding it (no successor opens)
          </label>
          {!retireInstead && (
            <>
              <SgdsInput placeholder="" label="Officer" hintText="e.g. OFF-01" value={officerId} required onSgdsInput={(e) => setOfficerId(targetValue(e))} />
              <SgdsInput placeholder="" label="Agency" hintText="e.g. MINDEF" value={agencyCode} required onSgdsInput={(e) => setAgencyCode(targetValue(e))} />
              <BoundedField
                label={BOUNDED_FIELDS.caseType}
                value={caseType}
                onValueChange={setCaseType}
                bounds={validation?.caseType}
                problem={problems.of.caseType}
                required
              />
              <div className="sm:col-span-2">
                <SgdsInput placeholder=""
                  label="Entitlement configuration (optional)"
                  value={entitlementConfiguration}
                  onSgdsInput={(e) => setEntitlementConfiguration(targetValue(e))}
                />
              </div>
            </>
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
